import "./SessionsExpanded.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Session, SessionDetails, fetchSessionDetails } from "../../api/sessions";
import { useGlobal } from "../../context/GlobalContext";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

interface SessionsExpandedProps {
    session: Session;
}

export function SessionsExpanded({ session }: SessionsExpandedProps) {
    const { selectedProjectKey, userKey } = useGlobal();
    const navigate = useNavigate();
    const [details, setDetails] = useState<SessionDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDetails = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setLoading(true);
            try {
                const data = await fetchSessionDetails(userKey, selectedProjectKey, session.sessionId);
                setDetails(data);
            } catch (err) {
                console.error('Failed to load session details:', err);
            } finally {
                setLoading(false);
            }
        };
        loadDetails();
    }, [session.sessionId, selectedProjectKey, userKey]);

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(details, null, 2));
    };

    const handleDownload = () => {
        const blob = new Blob([JSON.stringify(details, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${session.sessionId}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return <div className="session-loading">Loading session details...</div>;
    }

    return (
        <div className="session-expanded-page">
            <div className="session-expanded-header">
                <h1 className="session-expanded-title">{session.sessionId}</h1>
                <button className="back-button" onClick={() => navigate('/sessions')}>
                    ← Back to all sessions
                </button>
            </div>

            <div className="session-actions">
                <button 
                    className="action-icon-btn"
                    onClick={handleCopy}
                    title="Copy to clipboard"
                >
                    <ContentCopyIcon fontSize="small" />
                </button>
                <button 
                    className="action-icon-btn"
                    onClick={handleDownload}
                    title="Download JSON"
                >
                    <DownloadIcon fontSize="small" />
                </button>
            </div>

            <div className="session-details">
                <pre className="session-data">{JSON.stringify(details, null, 2)}</pre>
            </div>
        </div>
    );
}
